// TopBar — sticky header for the v2 dashboard shell.
//
// Slots: OrgSwitcher (left) → spacer → ⌘K trigger → NotificationsTray → ProfileMenu.
//
// ⌘K trigger: dispatches a synthetic Meta+K keydown on window. CHG-1223's
// global listener catches it and toggles the palette. The palette package
// (apps/dashboard-v2/src/components/palette/) is immutable per the mission
// brief, so the shell does NOT import usePalette directly — the synthetic
// dispatch is the contract.
//
// Per .forge/plans/v2-nav-shell.md.

import * as LucideIcons from "lucide-react";
import { useCallback, type ComponentType } from "react";
import { OrgSwitcher } from "./OrgSwitcher";
import { NotificationsTray } from "./NotificationsTray";
import { ProfileMenu } from "./ProfileMenu";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

export interface TopBarProps {
  // Optional override for tests so we can assert dispatch without binding
  // to a real palette. Production callers leave this undefined; the default
  // dispatches `keydown` to window.
  onOpenPalette?: () => void;
  // Optional sidebar-collapse toggle wired by AppShell.
  onToggleSidebar?: () => void;
}

export function TopBar({ onOpenPalette, onToggleSidebar }: TopBarProps) {
  const { Menu, Search } = LucideIcons as unknown as {
    Menu: IconCmp;
    Search: IconCmp;
  };

  const openPalette = useCallback(() => {
    if (onOpenPalette) {
      onOpenPalette();
      return;
    }
    // Synthetic Meta+K keydown — CHG-1223's window listener catches it.
    // Cmd on macOS, Ctrl elsewhere; meta=true works in both jsdom + browser
    // since the palette's isPaletteShortcut accepts either modifier.
    window.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
    );
  }, [onOpenPalette]);

  return (
    <header
      role="banner"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 20px",
        background: "var(--color-surface)",
        borderBottom: "1px solid var(--color-border-subtle)",
        height: 56,
        position: "sticky",
        top: 0,
        zIndex: 800,
      }}
    >
      {onToggleSidebar && (
        <button
          type="button"
          aria-label="Toggle sidebar"
          onClick={onToggleSidebar}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            border: "none",
            background: "transparent",
            color: "var(--color-text-secondary)",
            borderRadius: "var(--radius-md, 8px)",
            cursor: "pointer",
          }}
        >
          <Menu size={18} aria-hidden="true" />
        </button>
      )}
      <OrgSwitcher />
      <span style={{ flex: 1 }} />
      <button
        type="button"
        aria-label="Open command palette (press Command K or Control K)"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={openPalette}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          background: "var(--color-bg-subtle)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md, 8px)",
          color: "var(--color-text-muted)",
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
          minWidth: 220,
          justifyContent: "flex-start",
        }}
      >
        <Search size={14} aria-hidden="true" />
        <span style={{ flex: 1, textAlign: "left" }}>Search…</span>
        <kbd
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 10.5,
            padding: "1px 6px",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 4,
            color: "var(--color-text-muted)",
          }}
        >
          ⌘K
        </kbd>
      </button>
      <NotificationsTray />
      <ProfileMenu />
    </header>
  );
}
